import { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    Table,
    Button,
    Space,
    Input,
    Modal,
    Form,
    InputNumber,
    message,
    Tag,
    Card,
    Select,
    Switch,
    Row,
    Col,
    Dropdown,
    Upload,
    Tabs,
    Descriptions,
    Tooltip,
    Checkbox,
    AutoComplete,
    Divider,
    Statistic,
    Drawer,
    Grid,
    List,
} from 'antd';
import { PlusOutlined, SearchOutlined, EditOutlined, DownloadOutlined, UploadOutlined, MoreOutlined, DeleteOutlined, ExclamationCircleOutlined, InboxOutlined, InfoCircleOutlined, CopyOutlined, EyeOutlined } from '@ant-design/icons';
import { productsApi, adminApi, inventoryApi } from '../../api/client';
import { exportToCSV } from '../../utils/exportUtils';
import { parseCSV, mapCSVToProduct } from '../../utils/csvParser';
import { useAuthStore } from '../../store/auth.store';
import ProductBdoTab from './components/ProductBdoTab';
import dayjs from 'dayjs';

const { useBreakpoint } = Grid;

// Helper component to show batches for a product
function ProductBatchesTable({ productId }: { productId: string }) {
    const { data: batchesData, isLoading } = useQuery({
        queryKey: ['product-batches', productId],
        queryFn: () => inventoryApi.getBatches({ productId }).then((res: any) => res.data),
        enabled: !!productId,
    });

    const columns = [
        { title: 'LOT', dataIndex: 'lotNumber', key: 'lotNumber', width: 120 },
        { title: 'Lokalizacja', dataIndex: ['location', 'name'], key: 'location', render: (val: any, rec: any) => rec.location?.name || '-' },
        { title: 'Termin ważności', dataIndex: 'expiryDate', key: 'expiryDate', render: (d: string) => d ? dayjs(d).format('DD.MM.YYYY') : '-' },
        { title: 'Ilość początkowa', dataIndex: 'initialQuantity', key: 'initialQuantity', width: 120 },
        { title: 'Landed Cost', dataIndex: 'landedCost', key: 'landedCost', width: 120, render: (v: number) => `${parseFloat(String(v))?.toFixed(2) || '0.00'} zł` },
        { title: 'Status', dataIndex: 'status', key: 'status', render: (s: string) => <Tag color={s === 'AVAILABLE' ? 'green' : 'default'}>{s}</Tag> },
    ];

    return (
        <Table
            dataSource={batchesData || []}
            columns={columns}
            rowKey="id"
            loading={isLoading}
            size="small"
            pagination={false}
        />
    );
}

// Helper component to show variants for a product
function ProductVariantsTable({ productId }: { productId: string }) {
    const queryClient = useQueryClient();
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [editingVariant, setEditingVariant] = useState<any>(null);
    const [variantForm] = Form.useForm();

    const { data: variants, isLoading } = useQuery({
        queryKey: ['product-variants', productId],
        queryFn: () => productsApi.getVariants(productId).then((res: any) => res.data),
        enabled: !!productId,
    });

    const createMutation = useMutation({
        mutationFn: (data: any) => productsApi.createVariant(productId, data),
        onSuccess: () => {
            message.success('Wariant utworzony');
            queryClient.invalidateQueries({ queryKey: ['product-variants', productId] });
            setIsAddModalOpen(false);
            variantForm.resetFields();
        },
        onError: () => message.error('Błąd tworzenia wariantu'),
    });

    const updateMutation = useMutation({
        mutationFn: ({ id, data }: { id: string; data: any }) => productsApi.updateVariant(id, data),
        onSuccess: () => {
            message.success('Wariant zaktualizowany');
            queryClient.invalidateQueries({ queryKey: ['product-variants', productId] });
            setEditingVariant(null);
            variantForm.resetFields();
        },
        onError: () => message.error('Błąd aktualizacji wariantu'),
    });

    const deleteMutation = useMutation({
        mutationFn: (id: string) => productsApi.deleteVariant(id),
        onSuccess: () => {
            message.success('Wariant usunięty');
            queryClient.invalidateQueries({ queryKey: ['product-variants', productId] });
        },
        onError: () => message.error('Błąd usuwania wariantu'),
    });

    const handleSubmit = (values: any) => {
        if (editingVariant) {
            updateMutation.mutate({ id: editingVariant.id, data: values });
        } else {
            createMutation.mutate(values);
        }
    };

    const columns = [
        { title: 'SKU', dataIndex: 'sku', key: 'sku', width: 120 },
        { title: 'Nazwa', dataIndex: 'name', key: 'name' },
        {
            title: 'Cena',
            dataIndex: 'price',
            key: 'price',
            render: (v: any) => (v != null && !isNaN(Number(v))) ? `${Number(v).toFixed(2)} PLN` : '-'
        },
        {
            title: 'Status',
            dataIndex: 'isActive',
            key: 'isActive',
            render: (v: any) => <Tag color={v !== false ? 'green' : 'red'}>{v !== false ? 'Aktywny' : 'Nieaktywny'}</Tag>
        },
        {
            title: 'Akcje',
            key: 'actions',
            width: 100,
            render: (_: any, record: any) => (
                <Space>
                    <Button
                        size="small"
                        onClick={() => {
                            setEditingVariant(record);
                            variantForm.setFieldsValue(record);
                            setIsAddModalOpen(true);
                        }}
                    >
                        Edytuj
                    </Button>
                    <Button
                        size="small"
                        danger
                        onClick={() => {
                            Modal.confirm({
                                title: 'Usunąć wariant?',
                                content: `Wariant: ${record.name}`,
                                okText: 'Usuń',
                                okType: 'danger',
                                cancelText: 'Anuluj',
                                onOk: () => deleteMutation.mutate(record.id),
                            });
                        }}
                    >
                        Usuń
                    </Button>
                </Space>
            ),
        },
    ];

    return (
        <Space direction="vertical" style={{ width: '100%' }}>
            <Button
                type="primary"
                size="small"
                icon={<PlusOutlined />}
                onClick={() => {
                    setEditingVariant(null);
                    variantForm.resetFields();
                    setIsAddModalOpen(true);
                }}
            >
                Dodaj wariant
            </Button>
            <Table
                dataSource={variants || []}
                columns={columns}
                rowKey="id"
                loading={isLoading}
                size="small"
                pagination={false}
            />

            <Modal
                title={editingVariant ? 'Edytuj wariant' : 'Nowy wariant'}
                open={isAddModalOpen}
                onCancel={() => {
                    setIsAddModalOpen(false);
                    setEditingVariant(null);
                    variantForm.resetFields();
                }}
                onOk={() => variantForm.submit()}
                confirmLoading={createMutation.isPending || updateMutation.isPending}
            >
                <Form form={variantForm} layout="vertical" onFinish={handleSubmit}>
                    <Form.Item name="sku" label="SKU" rules={[{ required: true, message: 'Podaj SKU' }]}>
                        <Input placeholder="np. PROD-60" />
                    </Form.Item>
                    <Form.Item name="name" label="Nazwa wariantu" rules={[{ required: true, message: 'Podaj nazwę' }]}>
                        <Input placeholder="np. 60 kapsułek" />
                    </Form.Item>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="price" label="Cena (PLN)">
                                <InputNumber style={{ width: '100%' }} min={0} precision={2} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="costPrice" label="Koszt (PLN)">
                                <InputNumber style={{ width: '100%' }} min={0} precision={2} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item name="barcode" label="Kod kreskowy">
                        <Input placeholder="EAN/GTIN" />
                    </Form.Item>
                    <Form.Item name="isActive" label="Status" valuePropName="checked" initialValue={true}>
                        <Switch checkedChildren="Aktywny" unCheckedChildren="Nieaktywny" />
                    </Form.Item>
                </Form>
            </Modal>
        </Space>
    );
}

export default function ProductsPage() {
    const [searchText, setSearchText] = useState('');
    const [filterCategory, setFilterCategory] = useState<string | undefined>();
    const [filterStatus, setFilterStatus] = useState<string[]>([]);
    const [showArchived, setShowArchived] = useState(false);
    const [priceRange, setPriceRange] = useState<[number, number]>([0, 1000]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isImportModalOpen, setIsImportModalOpen] = useState(false);
    const [importData, setImportData] = useState<any[]>([]);
    const [importErrors, setImportErrors] = useState<string[]>([]);
    const [editingProduct, setEditingProduct] = useState<any>(null);
    const [form] = Form.useForm();
    const queryClient = useQueryClient();
    const { hasPermission } = useAuthStore();
    const canReadBdo = hasPermission('bdo:read') || hasPermission('bdo:manage');

    const screens = useBreakpoint();
    const isMobile = !screens.md;

    // Compute showArchived based on filter
    const shouldShowArchived = filterStatus.length === 0 || filterStatus.includes('ARCHIVED');

    const { data, isLoading } = useQuery({
        queryKey: ['products', searchText, filterStatus, shouldShowArchived],
        queryFn: () => productsApi.getAll({
            search: searchText,
            status: filterStatus.length === 1 ? filterStatus[0] : undefined,
            showArchived: shouldShowArchived
        }).then((res: any) => res.data),
    });

    // Calculate min/max prices from actual product data
    const priceMinMax = useMemo(() => {
        const products = data?.data || [];
        if (products.length === 0) return { min: 0, max: 1000 };
        const prices = products.filter((p: any) => p.basePrice > 0).map((p: any) => Number(p.basePrice));
        if (prices.length === 0) return { min: 0, max: 1000 };
        return {
            min: Math.floor(Math.min(...prices)),
            max: Math.ceil(Math.max(...prices)),
        };
    }, [data]);

    // Update price range when min/max changes
    useEffect(() => {
        setPriceRange([priceMinMax.min, priceMinMax.max]);
    }, [priceMinMax.min, priceMinMax.max]);

    const { data: categoriesData } = useQuery({
        queryKey: ['product-categories'],
        queryFn: () => productsApi.getAll({}).then((res) => {
            // Extract unique categories from products
            const cats = new Set<string>();
            res.data?.data?.forEach((p: any) => {
                if (p.category) cats.add(p.category);
            });
            return Array.from(cats);
        }),
    });

    const createMutation = useMutation({
        mutationFn: (data: any) => productsApi.create(data).then((res) => res.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['products'] });
            queryClient.invalidateQueries({ queryKey: ['product-categories'] });
            message.success('Produkt utworzony');
            setIsModalOpen(false);
            form.resetFields();
        },
        onError: (error: any) => {
            message.error(error.response?.data?.message || 'Błąd tworzenia produktu');
        },
    });

    const updateMutation = useMutation({
        mutationFn: ({ id, data }: { id: string; data: any }) =>
            productsApi.update(id, data).then((res) => res.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['products'] });
            queryClient.invalidateQueries({ queryKey: ['product-categories'] });
            message.success('Produkt zaktualizowany');
            setIsModalOpen(false);
            setEditingProduct(null);
            form.resetFields();
        },
        onError: (error: any) => {
            message.error(error.response?.data?.message || 'Błąd aktualizacji produktu');
        },
    });

    const deleteMutation = useMutation({
        mutationFn: (id: string) => productsApi.delete(id).then((res: any) => res.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['products'] });
            message.success('Produkt usunięty');
        },
        onError: (error: any) => {
            message.error(error.response?.data?.message || 'Błąd usuwania produktu');
        },
    });

    const handleSubmit = (values: any) => {
        if (editingProduct) {
            updateMutation.mutate({ id: editingProduct.id, data: values });
        } else {
            createMutation.mutate(values);
        }
    };

    const handleEdit = (record: any) => {
        setEditingProduct(record);
        form.setFieldsValue(record);
        setIsModalOpen(true);
    };

    const columns = [
        {
            title: '',
            dataIndex: 'imageUrl',
            key: 'image',
            width: 50,
            render: (url: string) => url ? (
                <img src={url} alt="" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }} />
            ) : (
                <div style={{ width: 40, height: 40, background: '#f0f0f0', borderRadius: 4 }} />
            ),
        },
        {
            title: 'SKU',
            dataIndex: 'sku',
            key: 'sku',
            width: 100,
            sorter: (a: any, b: any) => (a.sku || '').localeCompare(b.sku || ''),
        },
        {
            title: 'Nazwa',
            dataIndex: 'name',
            key: 'name',
            render: (name: string, record: any) => (
                <div>
                    <Space>
                        {name}
                        {record.customFields?.isAutoCreated && (
                            <Tooltip title="Produkt utworzony automatycznie podczas importu z Allegro">
                                <Tag color="blue" icon={<InfoCircleOutlined />} style={{ cursor: 'help' }}>Auto</Tag>
                            </Tooltip>
                        )}
                    </Space>
                    {record.shortName && (
                        <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 2 }}>
                            Skrót: {record.shortName}
                        </div>
                    )}
                </div>
            ),
            sorter: (a: any, b: any) => (a.name || '').localeCompare(b.name || ''),
        },
        {
            title: 'Kategoria',
            dataIndex: 'category',
            key: 'category',
            render: (cat: string) => cat && <Tag>{cat}</Tag>,
            sorter: (a: any, b: any) => (a.category || '').localeCompare(b.category || ''),
        },
        {
            title: 'Cena bazowa',
            dataIndex: 'basePrice',
            key: 'basePrice',
            render: (price: number) =>
                price
                    ? new Intl.NumberFormat('pl-PL', {
                        style: 'currency',
                        currency: 'PLN',
                    }).format(price)
                    : '-',
            sorter: (a: any, b: any) => (a.basePrice || 0) - (b.basePrice || 0),
        },
        {
            title: 'Cena zakupu',
            dataIndex: 'costPrice',
            key: 'costPrice',
            render: (price: number) =>
                price
                    ? new Intl.NumberFormat('pl-PL', {
                        style: 'currency',
                        currency: 'PLN',
                    }).format(price)
                    : <Tag color="red">Brak COGS!</Tag>,
            sorter: (a: any, b: any) => (a.costPrice || 0) - (b.costPrice || 0),
        },
        {
            title: 'Stan',
            dataIndex: 'totalStock',
            key: 'totalStock',
            width: 80,
            render: (stock: number) => (
                <Tag color={stock > 0 ? 'green' : 'red'}>
                    {stock || 0} szt
                </Tag>
            ),
            sorter: (a: any, b: any) => (a.totalStock || 0) - (b.totalStock || 0),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status: string) => {
                const colors: Record<string, string> = {
                    ACTIVE: 'green',
                    INACTIVE: 'orange',
                    ARCHIVED: 'default',
                };
                const labels: Record<string, string> = {
                    ACTIVE: 'Aktywny',
                    INACTIVE: 'Nieaktywny',
                    ARCHIVED: 'Archiwum',
                };
                return <Tag color={colors[status] || 'default'}>{labels[status] || status}</Tag>;
            },
            sorter: (a: any, b: any) => (a.status || '').localeCompare(b.status || ''),
        },
        {
            title: 'Akcje',
            key: 'actions',
            width: 100,
            render: (_: any, record: any) => (
                <Dropdown
                    menu={{
                        items: [
                            {
                                key: 'edit',
                                label: 'Edytuj',
                                icon: <EditOutlined />,
                                onClick: () => handleEdit(record),
                            },
                            {
                                key: 'duplicate',
                                label: 'Duplikuj',
                                icon: <CopyOutlined />,
                                onClick: () => {
                                    productsApi.duplicate(record.id).then(() => {
                                        message.success('Produkt zduplikowany');
                                        queryClient.invalidateQueries({ queryKey: ['products'] });
                                    }).catch(() => {
                                        message.error('Błąd duplikowania produktu');
                                    });
                                },
                            },
                            {
                                key: 'archive',
                                label: record.isArchived ? 'Przywróć z archiwum' : 'Archiwizuj',
                                onClick: () => {
                                    if (record.isArchived) {
                                        // Restore from archive
                                        updateMutation.mutate({
                                            id: record.id,
                                            data: { isArchived: false, status: 'ACTIVE' },
                                        });
                                    } else if (record.isActive) {
                                        // Archive active product - confirm deactivation
                                        Modal.confirm({
                                            title: 'Archiwizować produkt?',
                                            icon: <ExclamationCircleOutlined />,
                                            content: 'Produkt jest aktywny. Archiwizacja automatycznie dezaktywuje produkt.',
                                            okText: 'Archiwizuj',
                                            cancelText: 'Anuluj',
                                            onOk: () => {
                                                updateMutation.mutate({
                                                    id: record.id,
                                                    data: { isArchived: true, isActive: false, status: 'ARCHIVED' },
                                                });
                                            },
                                        });
                                    } else {
                                        // Archive inactive product - just archive
                                        updateMutation.mutate({
                                            id: record.id,
                                            data: { isArchived: true, status: 'ARCHIVED' },
                                        });
                                    }
                                },
                            },
                            { type: 'divider' },
                            {
                                key: 'delete',
                                label: 'Usuń',
                                icon: <DeleteOutlined />,
                                danger: true,
                                onClick: () => {
                                    Modal.confirm({
                                        title: 'Usunąć produkt?',
                                        icon: <ExclamationCircleOutlined />,
                                        content: `Produkt: ${record.name}. Ta operacja jest nieodwracalna.`,
                                        okText: 'Usuń',
                                        okType: 'danger',
                                        cancelText: 'Anuluj',
                                        onOk: () => deleteMutation.mutate(record.id),
                                    });
                                },
                            },
                        ],
                    }}
                    trigger={['click']}
                >
                    <Button icon={<MoreOutlined />} size="small" />
                </Dropdown>
            ),
        },
    ];

    const categoryOptions = (categoriesData || []).map((cat: string) => ({
        label: cat,
        value: cat,
    }));

    // Apply client-side filters
    const filteredData = (data?.data || []).filter((product: any) => {
        if (filterCategory && product.category !== filterCategory) return false;
        if (product.basePrice && (product.basePrice < priceRange[0] || product.basePrice > priceRange[1])) return false;
        // Filter by status if multiple selected (server only filters single status)
        if (filterStatus.length > 0 && !filterStatus.includes(product.status)) return false;
        return true;
    });

    const actionButtons = (
        <Space wrap={isMobile} style={{ width: isMobile ? '100%' : 'auto' }}>
            <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                    setEditingProduct(null);
                    form.resetFields();
                    setIsModalOpen(true);
                }}
                style={{ flex: isMobile ? 1 : 'none' }}
            >
                Dodaj produkt
            </Button>
            <Dropdown
                menu={{
                    items: [
                        {
                            key: 'csv',
                            label: 'Eksportuj CSV',
                            onClick: () => {
                                exportToCSV(
                                    filteredData,
                                    'produkty',
                                    [
                                        { key: 'sku', title: 'SKU' },
                                        { key: 'name', title: 'Nazwa' },
                                        { key: 'category', title: 'Kategoria' },
                                        { key: 'basePrice', title: 'Cena bazowa' },
                                        { key: 'costPrice', title: 'Cena zakupu' },
                                        { key: 'totalStock', title: 'Stan' },
                                        { key: 'status', title: 'Status' },
                                    ]
                                );
                            },
                        },
                        {
                            key: 'xlsx',
                            label: 'Eksportuj XLSX (Excel)',
                            onClick: async () => {
                                const { exportToXLSX } = await import('../../utils/exportUtils');
                                await exportToXLSX(
                                    filteredData,
                                    'produkty',
                                    [
                                        { key: 'sku', title: 'SKU' },
                                        { key: 'name', title: 'Nazwa' },
                                        { key: 'category', title: 'Kategoria' },
                                        { key: 'basePrice', title: 'Cena bazowa' },
                                        { key: 'costPrice', title: 'Cena zakupu' },
                                        { key: 'totalStock', title: 'Stan' },
                                        { key: 'status', title: 'Status' },
                                    ]
                                );
                            },
                        },
                    ],
                }}
            >
                <Button icon={<DownloadOutlined />} style={{ flex: isMobile ? 1 : 'none' }}>
                    Eksportuj {!isMobile && '▾'}
                </Button>
            </Dropdown>
            <Button
                icon={<UploadOutlined />}
                onClick={() => setIsImportModalOpen(true)}
                style={{ flex: isMobile ? 1 : 'none' }}
            >
                Importuj
            </Button>
        </Space>
    );

    return (
        <Card
            title="Produkty"
            extra={!isMobile && actionButtons}
        >
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                {isMobile && actionButtons}
                
                <Row gutter={[16, 16]}>
                    <Col xs={24} md={6}>
                        <Input
                            placeholder="Szukaj SKU lub nazwy..."
                            prefix={<SearchOutlined />}
                            value={searchText}
                            onChange={(e: any) => setSearchText(e.target.value)}
                            allowClear
                        />
                    </Col>
                    <Col xs={24} md={6}>
                        <Select
                            placeholder="Filtruj wg kategorii"
                            options={categoryOptions}
                            value={filterCategory}
                            onChange={setFilterCategory}
                            allowClear
                            style={{ width: '100%' }}
                        />
                    </Col>
                    <Col xs={24} md={8}>
                        <Row gutter={8}>
                            <Col span={12}>
                                <InputNumber
                                    placeholder="Cena od"
                                    min={0}
                                    value={priceRange[0]}
                                    onChange={(val) => setPriceRange([(val ?? priceMinMax.min), priceRange[1]])}
                                    addonBefore="Od"
                                    style={{ width: '100%' }}
                                />
                            </Col>
                            <Col span={12}>
                                <InputNumber
                                    placeholder="Cena do"
                                    min={0}
                                    value={priceRange[1]}
                                    onChange={(val) => setPriceRange([priceRange[0], (val ?? priceMinMax.max)])}
                                    addonBefore="Do"
                                    style={{ width: '100%' }}
                                />
                            </Col>
                        </Row>
                    </Col>
                    <Col xs={24} md={4}>
                        <Select
                            mode="multiple"
                            placeholder="Status"
                            allowClear
                            style={{ width: '100%' }}
                            value={filterStatus}
                            options={[
                                { value: 'ACTIVE', label: 'Aktywne' },
                                { value: 'INACTIVE', label: 'Nieaktywne' },
                                { value: 'ARCHIVED', label: 'Archiwalne' },
                            ]}
                            onChange={(val) => setFilterStatus(val || [])}
                            maxTagCount="responsive"
                        />
                    </Col>
                </Row>

                {isMobile ? (
                    <List
                        dataSource={filteredData}
                        loading={isLoading}
                        pagination={{
                            pageSize: 10,
                            size: 'small',
                            showTotal: (total, range) => `${range[0]}-${range[1]}/${total}`,
                        }}
                        renderItem={(product: any) => (
                            <List.Item style={{ padding: '8px 0' }}>
                                <Card
                                    size="small"
                                    hoverable
                                    onClick={() => handleEdit(product)}
                                    style={{ width: '100%', borderRadius: 8 }}
                                >
                                    <div style={{ display: 'flex', gap: 12 }}>
                                        {product.imageUrl ? (
                                            <img src={product.imageUrl} alt="" style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 4 }} />
                                        ) : (
                                            <div style={{ width: 60, height: 60, background: '#f0f0f0', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <InboxOutlined style={{ fontSize: 24, color: '#ccc' }} />
                                            </div>
                                        )}
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {product.name}
                                            </div>
                                            <div style={{ fontSize: 12, color: '#999', marginBottom: 4 }}>
                                                {product.sku} {product.category && <Tag size="small" style={{ margin: 0, fontSize: 10 }}>{product.category}</Tag>}
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                                                <div>
                                                    <div style={{ fontWeight: 700, fontSize: 15 }}>
                                                        {new Intl.NumberFormat('pl-PL', { style: 'currency', currency: 'PLN' }).format(product.basePrice || 0)}
                                                    </div>
                                                </div>
                                                <div style={{ textAlign: 'right' }}>
                                                    <Tag color={product.totalStock > 0 ? 'green' : 'red'} style={{ margin: 0 }}>
                                                        {product.totalStock || 0} szt
                                                    </Tag>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </Card>
                            </List.Item>
                        )}
                    />
                ) : (
                    <Table
                        dataSource={filteredData}
                        columns={columns}
                        rowKey="id"
                        loading={isLoading}
                        pagination={{ pageSize: 20, total: data?.total }}
                        scroll={{ x: 1000 }}
                        onRow={(record) => ({
                            onClick: () => handleEdit(record),
                            style: { cursor: 'pointer' },
                        })}
                    />
                )}
            </Space>

            <Modal
                title={editingProduct ? 'Edytuj produkt' : 'Nowy produkt'}
                open={isModalOpen}
                onCancel={() => {
                    setIsModalOpen(false);
                    setEditingProduct(null);
                    form.resetFields();
                }}
                onOk={() => form.submit()}
                confirmLoading={createMutation.isPending || updateMutation.isPending}
                width={isMobile ? '100%' : 800}
                style={isMobile ? { top: 0, margin: 0, maxWidth: '100vw' } : {}}
            >
                <Form form={form} layout="vertical" onFinish={handleSubmit}>
                    <Tabs
                        defaultActiveKey="basic"
                        items={[
                            {
                                key: 'basic',
                                label: 'Podstawowe',
                                children: (
                                    <Row gutter={16}>
                                        <Col span={12}>
                                            <Form.Item
                                                name="sku"
                                                label="SKU"
                                                rules={[{ required: true, message: 'Podaj SKU' }]}
                                            >
                                                <Input />
                                            </Form.Item>
                                        </Col>
                                        <Col span={12}>
                                            <Form.Item
                                                name="name"
                                                label="Nazwa"
                                                rules={[{ required: true, message: 'Podaj nazwę' }]}
                                            >
                                                <Input />
                                            </Form.Item>
                                        </Col>
                                        <Col span={12}>
                                            <Form.Item
                                                name="shortName"
                                                label="Nazwa skrócona"
                                                tooltip="Krótka nazwa używana na gęstych listach, np. w tabeli zamówień. Jeśli pusta, system użyje nazwy produktu."
                                            >
                                                <Input
                                                    maxLength={40}
                                                    allowClear
                                                    placeholder="np. Shilajit 50g"
                                                />
                                            </Form.Item>
                                        </Col>
                                        <Col span={12}>
                                            <Form.Item name="category" label="Kategoria">
                                                <AutoComplete
                                                    placeholder="Wpisz lub wybierz kategorię"
                                                    options={categoryOptions}
                                                    allowClear
                                                    filterOption={(input, option) =>
                                                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                                    }
                                                />
                                            </Form.Item>
                                        </Col>
                                        <Col span={12}>
                                            <Form.Item name="status" label="Status" initialValue="ACTIVE">
                                                <Select
                                                    options={[
                                                        { value: 'ACTIVE', label: 'Aktywny' },
                                                        { value: 'INACTIVE', label: 'Nieaktywny' },
                                                        { value: 'ARCHIVED', label: 'Archiwum' },
                                                    ]}
                                                />
                                            </Form.Item>
                                        </Col>
                                        <Col span={24}>
                                            <Form.Item name="description" label="Opis">
                                                <Input.TextArea rows={3} />
                                            </Form.Item>
                                        </Col>
                                        <Col span={24}>
                                            <Form.Item label="Obrazek produktu">
                                                <Upload.Dragger
                                                    name="image"
                                                    accept="image/*"
                                                    maxCount={1}
                                                    beforeUpload={(file: any) => {
                                                        const reader = new FileReader();
                                                        reader.onload = (e) => {
                                                            const base64 = e.target?.result as string;
                                                            form.setFieldValue('imageUrl', base64);
                                                        };
                                                        reader.readAsDataURL(file);
                                                        return false;
                                                    }}
                                                    onRemove={() => form.setFieldValue('imageUrl', '')}
                                                >
                                                    <p className="ant-upload-drag-icon">
                                                        <InboxOutlined />
                                                    </p>
                                                    <p className="ant-upload-text">Kliknij lub przeciągnij plik</p>
                                                </Upload.Dragger>
                                                <div style={{ marginTop: 8, marginBottom: 8, textAlign: 'center' }}>lub podaj URL:</div>
                                                <Form.Item name="imageUrl" noStyle>
                                                    <Input placeholder="https://example.com/image.jpg" />
                                                </Form.Item>
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                ),
                            },
                            {
                                key: 'prices',
                                label: 'Ceny i koszty',
                                children: (
                                    <Space direction="vertical" style={{ width: '100%' }}>
                                        <Row gutter={16}>
                                            <Col span={12}>
                                                <Form.Item name="basePrice" label="Cena bazowa (sprzedaż)">
                                                    <InputNumber style={{ width: '100%' }} min={0} precision={2} addonAfter="PLN" />
                                                </Form.Item>
                                            </Col>
                                            <Col span={12}>
                                                <Form.Item
                                                    name="costPrice"
                                                    label={
                                                        <Tooltip title="Domyślny koszt jednostkowy (używany gdy brak partii). Rzeczywiste koszty przypisane są do partii.">
                                                            <span>Koszt domyślny <InfoCircleOutlined style={{ color: '#999' }} /></span>
                                                        </Tooltip>
                                                    }
                                                >
                                                    <InputNumber style={{ width: '100%' }} min={0} precision={2} addonAfter="PLN" />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                        <Descriptions bordered size="small" column={1}>
                                            <Descriptions.Item label="Uwaga">
                                                Rzeczywiste koszty zakupu są przypisane do <strong>partii (LOT)</strong>, nie produktu.
                                                Koszt domyślny jest używany tylko gdy produkt nie ma partii.
                                            </Descriptions.Item>
                                            {editingProduct && (
                                                <Descriptions.Item label="Koszt z partii">
                                                    Średni landed cost z aktywnych partii będzie widoczny w zakładce "Partie".
                                                </Descriptions.Item>
                                            )}
                                        </Descriptions>
                                    </Space>
                                ),
                            },
                            {
                                key: 'variants',
                                label: 'Warianty',
                                disabled: !editingProduct,
                                children: editingProduct ? (
                                    <ProductVariantsTable productId={editingProduct?.id} />
                                ) : (
                                    <p style={{ color: '#999' }}>Zapisz produkt, aby móc zarządzać wariantami.</p>
                                ),
                            },
                            ...(canReadBdo
                                ? [
                                      {
                                          key: 'bdo',
                                          label: 'BDO',
                                          disabled: !editingProduct,
                                          children: editingProduct ? (
                                              <ProductBdoTab productId={editingProduct.id} />
                                          ) : (
                                              <p style={{ color: '#999' }}>
                                                  Zapisz produkt, aby móc zarządzać opakowaniem BDO.
                                              </p>
                                          ),
                                      },
                                  ]
                                : []),
                            {
                                key: 'batches',
                                label: 'Partie (LOT)',
                                disabled: !editingProduct,
                                children: editingProduct ? (
                                    <Space direction="vertical" style={{ width: '100%' }}>
                                        <p style={{ color: '#666', marginBottom: 16 }}>
                                            Partie tego produktu. Zarządzaj partiami w zakładce Magazyn → Partie.
                                        </p>
                                        <ProductBatchesTable productId={editingProduct?.id} />
                                    </Space>
                                ) : (
                                    <p style={{ color: '#999' }}>Zapisz produkt, aby móc zarządzać partiami.</p>
                                ),
                            },
                            {
                                key: 'stock',
                                label: 'Stany magazynowe',
                                children: (
                                    <Space direction="vertical" style={{ width: '100%' }}>
                                        <Row gutter={16}>
                                            <Col span={12}>
                                                <Form.Item
                                                    name="minStockLevel"
                                                    label={
                                                        <Tooltip title="Minimalna ilość - poniżej tej wartości system generuje alert">
                                                            <span>Minimalny stan <InfoCircleOutlined style={{ color: '#999' }} /></span>
                                                        </Tooltip>
                                                    }
                                                >
                                                    <InputNumber style={{ width: '100%' }} min={0} placeholder="np. 10" />
                                                </Form.Item>
                                            </Col>
                                            <Col span={12}>
                                                <Form.Item
                                                    name="optimalOrderQty"
                                                    label={
                                                        <Tooltip title="Sugerowana ilość do zamówienia u dostawcy">
                                                            <span>Optymalna ilość zamówienia <InfoCircleOutlined style={{ color: '#999' }} /></span>
                                                        </Tooltip>
                                                    }
                                                >
                                                    <InputNumber style={{ width: '100%' }} min={0} placeholder="np. 100" />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                        {editingProduct && (
                                            <Card size="small" title="Aktualny stan" style={{ marginTop: 16 }}>
                                                <p style={{ color: '#666' }}>
                                                    Stan magazynowy jest obliczany z eventów inwentarza.
                                                    Zobacz zakładkę Magazyn → Stany magazynowe.
                                                </p>
                                            </Card>
                                        )}
                                    </Space>
                                ),
                            },
                            {
                                key: 'shipping',
                                label: 'Wysyłka',
                                children: (
                                    <Space direction="vertical" style={{ width: '100%' }}>
                                        <p style={{ color: '#666', marginBottom: 16 }}>
                                            Dane do kalkulacji wysłki i obsługi magazynowej.
                                        </p>
                                        <Row gutter={16}>
                                            <Col span={12}>
                                                <Form.Item name="weight" label="Waga (g)">
                                                    <InputNumber style={{ width: '100%' }} min={0} placeholder="np. 250" addonAfter="g" />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                        <Row gutter={16}>
                                            <Col span={8}>
                                                <Form.Item name="length" label="Długość (cm)">
                                                    <InputNumber style={{ width: '100%' }} min={0} placeholder="30" addonAfter="cm" />
                                                </Form.Item>
                                            </Col>
                                            <Col span={8}>
                                                <Form.Item name="width" label="Szerokość (cm)">
                                                    <InputNumber style={{ width: '100%' }} min={0} placeholder="20" addonAfter="cm" />
                                                </Form.Item>
                                            </Col>
                                            <Col span={8}>
                                                <Form.Item name="height" label="Wysokość (cm)">
                                                    <InputNumber style={{ width: '100%' }} min={0} placeholder="10" addonAfter="cm" />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                    </Space>
                                ),
                            },
                        ]}
                    />
                </Form>
            </Modal>

            {/* CSV Import Modal */}
            <Modal
                title="Import produktów z CSV"
                open={isImportModalOpen}
                onCancel={() => {
                    setIsImportModalOpen(false);
                    setImportData([]);
                    setImportErrors([]);
                }}
                width={800}
                footer={[
                    <Button key="cancel" onClick={() => setIsImportModalOpen(false)}>
                        Anuluj
                    </Button>,
                    <Button
                        key="import"
                        type="primary"
                        disabled={importData.length === 0 || importErrors.length > 0}
                        onClick={async () => {
                            try {
                                let success = 0;
                                let failed = 0;
                                for (const product of importData) {
                                    try {
                                        await productsApi.create(product);
                                        success++;
                                    } catch {
                                        failed++;
                                    }
                                }
                                message.success(`Zaimportowano ${success} produktów${failed > 0 ? `, ${failed} błędów` : ''}`);
                                setIsImportModalOpen(false);
                                setImportData([]);
                                queryClient.invalidateQueries({ queryKey: ['products'] });
                            } catch {
                                message.error('Błąd importu');
                            }
                        }}
                    >
                        Importuj ({importData.length})
                    </Button>,
                ]}
            >
                <Space direction="vertical" style={{ width: '100%' }}>
                    <input
                        type="file"
                        accept=".csv"
                        id="csv-upload"
                        style={{ display: 'none' }}
                        onChange={(e: any) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            const reader = new FileReader();
                            reader.onload = (event) => {
                                const content = event.target?.result as string;
                                const parsed = parseCSV(content, ['SKU', 'Nazwa']);
                                setImportErrors(parsed.errors);
                                setImportData(parsed.data.map(mapCSVToProduct));
                            };
                            reader.readAsText(file, 'UTF-8');
                            e.target.value = '';
                        }}
                    />
                    <Button
                        icon={<UploadOutlined />}
                        onClick={() => document.getElementById('csv-upload')?.click()}
                    >
                        Wybierz plik CSV
                    </Button>

                    {importErrors.length > 0 && (
                        <div style={{ color: '#ff4d4f' }}>
                            {importErrors.map((err, i) => (
                                <div key={i}>⚠️ {err}</div>
                            ))}
                        </div>
                    )}

                    {importData.length > 0 && (
                        <Table
                            dataSource={importData}
                            columns={[
                                { title: 'SKU', dataIndex: 'sku', key: 'sku' },
                                { title: 'Nazwa', dataIndex: 'name', key: 'name' },
                                { title: 'Kategoria', dataIndex: 'category', key: 'category' },
                                { title: 'Cena', dataIndex: 'basePrice', key: 'basePrice' },
                            ]}
                            rowKey={(r: any, i: any) => `${r.sku}-${i}`}
                            size="small"
                            pagination={{ pageSize: 5 }}
                        />
                    )}

                    <div style={{ fontSize: 12, color: '#999' }}>
                        Wymagane kolumny: SKU, Nazwa<br />
                        Opcjonalne: Kategoria, Cena bazowa, Cena zakupu, Opis, Marka, Aktywny
                    </div>
                </Space>
            </Modal>
        </Card>
    );
}
